"use client"

import React, { useState, useMemo, memo } from "react"
import { FixedSizeList as List, areEqual } from "react-window"
import { Checkbox } from "./checkbox"
import { Input } from "./input"
import { Label } from "./label"
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "./popover"
import { cn } from "../../lib/utils"
import { ChevronDown, Filter, X } from "lucide-react"
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "./select"
import { Button } from "./button"
import { Calendar } from "./calendar"
import { Popover as CalendarPopover, PopoverContent as CalendarPopoverContent, PopoverTrigger as CalendarPopoverTrigger } from "./popover"
import { format } from "date-fns"
import type { DateRange } from "react-day-picker"

export interface FilterableOption {
    id: string
    name: string
    role?: string
    organization?: string
    serveDates?: Array<{ from: string; to: string }> | null
}

// Virtualized row renderer
const Row = memo(
    ({
        index,
        style,
        data,
    }: {
        index: number
        style: React.CSSProperties
        data: {
            filteredOptions: FilterableOption[]
            selectedValues: string[]
            handleToggle: (value: string) => void
        }
    }) => {
        const { filteredOptions, selectedValues, handleToggle } = data
        const option = filteredOptions[index]
        if (!option) return null

        return (
            <div
                style={style}
                className="flex items-center space-x-2 p-2 hover:bg-accent rounded-sm cursor-pointer"
                onClick={() => handleToggle(option.id)}
            >
                <Checkbox
                    checked={selectedValues.includes(option.id)}
                    className="rounded-[5px] border-primary pointer-events-none"
                />
                <div className="flex flex-col flex-1 min-w-0">
                    <span className="text-sm text-foreground font-medium">
                        {option.name}
                    </span>
                    {(option.organization || option.role) && (
                        <div className="flex items-center gap-2 text-xs text-muted-foreground">
                            {option.organization && (
                                <span>{option.organization}</span>
                            )}
                            {option.organization && option.role && <span>•</span>}
                            {option.role && <span>{option.role}</span>}
                        </div>
                    )}
                </div>
            </div>
        )
    },
    areEqual
)

export const MultiSelectWithFilters = ({
    options,
    selectedValues,
    onSelectionChange,
    placeholder,
    searchPlaceholder = "Search...",
    customHeight,
    organizationOptions = [],
}: {
    options: FilterableOption[]
    selectedValues: string[]
    onSelectionChange: (values: string[]) => void
    placeholder: string
    searchPlaceholder?: string
    customHeight?: string
    organizationOptions?: string[]
}) => {
    const [isOpen, setIsOpen] = useState(false)
    const [searchTerm, setSearchTerm] = useState("")
    const [selectedOrganization, setSelectedOrganization] = useState<string>("all")
    const [organizationSearchTerm, setOrganizationSearchTerm] = useState("")
    const [selectedDateRange, setSelectedDateRange] = useState<DateRange | undefined>(undefined)
    const [isDatePickerOpen, setIsDatePickerOpen] = useState(false)

    // Filter options based on search, organization, and date
    const filteredOptions = useMemo(() => {
        let filtered = options

        // Filter by search term (name, organization, role)
        if (searchTerm) {
            const searchLower = searchTerm.toLowerCase()
            filtered = filtered.filter(
                (option) =>
                    option.name.toLowerCase().includes(searchLower) ||
                    (option.organization && option.organization.toLowerCase().includes(searchLower)) ||
                    (option.role && option.role.toLowerCase().includes(searchLower))
            )
        }

        // Filter by organization
        if (selectedOrganization && selectedOrganization !== "all") {
            filtered = filtered.filter(
                (option) => option.organization === selectedOrganization
            )
        }

        // Filter by service periods that overlap the selected date range.
        if (selectedDateRange?.from) {
            const rangeStart = format(selectedDateRange.from, "yyyy-MM-dd")
            const rangeEnd = format(selectedDateRange.to ?? selectedDateRange.from, "yyyy-MM-dd")
            filtered = filtered.filter((option) => {
                return option.serveDates?.some(({ from, to }) => {
                    const start = from?.slice(0, 10)
                    const end = to?.slice(0, 10)
                    return Boolean(start && end && start <= rangeEnd && end >= rangeStart)
                }) ?? false
            })
        }

        return filtered
    }, [options, searchTerm, selectedOrganization, selectedDateRange])

    const handleToggle = (value: string) => {
        const newSelection = selectedValues.includes(value)
            ? selectedValues.filter((v) => v !== value)
            : [...selectedValues, value]
        onSelectionChange(newSelection)
    }

    // Map selected values to include both ID and name for removal
    const selectedItems = selectedValues
        .map((id) => {
            const option = options.find((opt) => opt.id === id)
            return option ? { id, name: option.name } : null
        })
        .filter((item): item is { id: string; name: string } => item !== null)

    // For custom height (taller volunteers field), show all names
    // For regular height, show only first name with counter
    const displayItems = customHeight ? selectedItems : selectedItems.slice(0, 1)
    const remainingCount = customHeight ? 0 : selectedItems.length - 1

    // Handler to remove a specific volunteer
    const handleRemove = (idToRemove: string, e: React.MouseEvent) => {
        e.stopPropagation() // Prevent opening the popover
        const newSelection = selectedValues.filter((v) => v !== idToRemove)
        onSelectionChange(newSelection)
    }

    // Check if any filters are active
    const hasActiveFilters = selectedOrganization !== "all" || selectedDateRange?.from !== undefined

    // Clear all filters
    const clearFilters = () => {
        setSelectedOrganization("all")
        setSelectedDateRange(undefined)
        setSearchTerm("")
        setOrganizationSearchTerm("")
    }

    // Row component moved outside to prevent unmounting/recreation on re-render

    return (
        <Popover
            open={isOpen}
            onOpenChange={(open) => {
                setIsOpen(open)
                if (!open) {
                    setSearchTerm("")
                }
            }}
        >
            <PopoverTrigger asChild>
                <div
                    role="combobox"
                    aria-expanded={isOpen}
                    aria-controls="combobox-popover-content"
                    tabIndex={0}
                    onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault()
                            setIsOpen(!isOpen)
                        }
                    }}
                    className={cn(
                        "flex w-full justify-between rounded border border-input bg-white px-3 py-2 text-sm shadow-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none disabled:cursor-not-allowed disabled:opacity-50 cursor-pointer data-[placeholder]:text-muted-foreground",
                        customHeight ? `${customHeight} items-start` : "h-14 items-center whitespace-nowrap [&>span]:line-clamp-1",
                        isOpen && "border-primary",
                        "focus:border-primary"
                    )}
                >
                    <div className={cn(
                        "flex items-start gap-1",
                        customHeight ? "flex-wrap overflow-y-auto flex-1 pr-2" : "flex-nowrap items-center"
                    )}
                    style={customHeight ? { maxHeight: 'calc(100% - 4px)' } : undefined}
                    >
                        {selectedItems.length > 0 ? (
                            <>
                                {displayItems.map((item) => (
                                    <span
                                        key={item.id}
                                        className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-muted/50 text-muted-foreground border border-muted"
                                    >
                                        {item.name}
                                        <button
                                            type="button"
                                            onClick={(e) => handleRemove(item.id, e)}
                                            className="ml-0.5 hover:bg-muted rounded-full p-0.5 flex items-center justify-center transition-colors"
                                            aria-label={`Remove ${item.name}`}
                                        >
                                            <X className="h-3 w-3" />
                                        </button>
                                    </span>
                                ))}
                                {remainingCount > 0 && (
                                    <span className="inline-flex items-center justify-center w-7 h-7 rounded-full text-xs font-medium bg-muted text-muted-foreground">
                                        +{remainingCount}
                                    </span>
                                )}
                            </>
                        ) : (
                            <span className="text-muted-foreground/60">
                                {placeholder}
                            </span>
                        )}
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                        {hasActiveFilters && (
                            <div className="h-2 w-2 rounded-full bg-primary" />
                        )}
                        <ChevronDown className="h-4 w-4 opacity-50" />
                    </div>
                </div>
            </PopoverTrigger>
            <PopoverContent className="w-full p-0" align="start">
                <div className="p-2 space-y-2">
                    {/* Search Input */}
                    <Input
                        placeholder={searchPlaceholder}
                        value={searchTerm}
                        onChange={(e) =>
                            setSearchTerm((e.target as HTMLInputElement).value)
                        }
                        className="h-9 text-sm"
                    />

                    {/* Filters Row */}
                    <div className="flex flex-col gap-2">
                        <div className="flex items-center justify-between">
                            <Label className="text-xs font-semibold text-muted-foreground flex items-center gap-1">
                                <Filter className="h-3 w-3" />
                                Filters
                            </Label>
                            {hasActiveFilters && (
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-6 px-2 text-xs"
                                    onClick={clearFilters}
                                >
                                    <X className="h-3 w-3 mr-1" />
                                    Clear
                                </Button>
                            )}
                        </div>

                        <div className="grid grid-cols-2 gap-2">
                            {/* Organization Filter */}
                            <div className="space-y-1">
                                <Label className="text-xs text-muted-foreground">
                                    Organization
                                </Label>
                                <Select
                                    value={selectedOrganization}
                                    onValueChange={setSelectedOrganization}
                                >
                                    <SelectTrigger className="h-8 text-xs">
                                        <SelectValue placeholder="All" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <div className="p-1">
                                            <Input
                                                value={organizationSearchTerm}
                                                onChange={(e) =>
                                                    setOrganizationSearchTerm(
                                                        (e.target as HTMLInputElement).value
                                                    )
                                                }
                                                onPointerDown={(e) => e.stopPropagation()}
                                                onClick={(e) => {
                                                    e.stopPropagation()
                                                    e.currentTarget.focus()
                                                }}
                                                onKeyDown={(e) => e.stopPropagation()}
                                                placeholder="Search organizations..."
                                                className="h-8 text-xs"
                                            />
                                        </div>
                                        <SelectItem value="all">All Organizations</SelectItem>
                                        {organizationOptions
                                            .filter((org) =>
                                                org
                                                    .toLowerCase()
                                                    .includes(organizationSearchTerm.toLowerCase())
                                            )
                                            .map((org) => (
                                            <SelectItem key={org} value={org}>
                                                {org}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>

                            {/* Service Date Range Filter */}
                            <div className="space-y-1">
                                <Label className="text-xs text-muted-foreground">
                                    Service dates
                                </Label>
                                <CalendarPopover
                                    open={isDatePickerOpen}
                                    onOpenChange={setIsDatePickerOpen}
                                >
                                    <CalendarPopoverTrigger asChild>
                                        <Button
                                            variant="outline"
                                            className={cn(
                                                "h-8 w-full justify-start text-left text-xs",
                                                !selectedDateRange?.from && "text-muted-foreground"
                                            )}
                                        >
                                            {selectedDateRange?.from ? (
                                                selectedDateRange.to ? (
                                                    `${format(selectedDateRange.from, "MMM d")} – ${format(selectedDateRange.to, "MMM d, yyyy")}`
                                                ) : (
                                                    `${format(selectedDateRange.from, "MMM d, yyyy")} – ...`
                                                )
                                            ) : (
                                                "Any dates"
                                            )}
                                        </Button>
                                    </CalendarPopoverTrigger>
                                    <CalendarPopoverContent className="w-auto p-0" align="start">
                                        <Calendar
                                            mode="range"
                                            selected={selectedDateRange}
                                            onSelect={setSelectedDateRange}
                                            initialFocus
                                        />
                                        <div className="flex gap-2 p-2 border-t">
                                            {selectedDateRange?.from && (
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    className="flex-1 h-7 text-xs"
                                                    onClick={() => setSelectedDateRange(undefined)}
                                                >
                                                    Clear dates
                                                </Button>
                                            )}
                                            <Button
                                                variant="outline"
                                                size="sm"
                                                className="flex-1 h-7 text-xs"
                                                onClick={() => setIsDatePickerOpen(false)}
                                            >
                                                Done
                                            </Button>
                                        </div>
                                    </CalendarPopoverContent>
                                </CalendarPopover>
                            </div>
                        </div>
                    </div>

                    {/* Results Count */}
                    {hasActiveFilters && (
                        <div className="text-xs text-muted-foreground px-1">
                            {filteredOptions.length} {filteredOptions.length === 1 ? 'result' : 'results'}
                        </div>
                    )}

                    {/* Options List */}
                    <div className="max-h-60">
                        {filteredOptions.length > 0 ? (
                            <List
                                height={Math.min(240, filteredOptions.length * 50)} // Dynamic height based on items
                                itemCount={filteredOptions.length}
                                itemSize={50} // Increased to accommodate organization/role
                                width="100%"
                                className="scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100"
                                itemData={{
                                    filteredOptions,
                                    selectedValues,
                                    handleToggle,
                                }}
                            >
                                {Row}
                            </List>
                        ) : (
                            <div className="p-4 text-center text-sm text-muted-foreground">
                                No results found
                            </div>
                        )}
                    </div>
                    <div className="border-t pt-2">
                        <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            className="w-full h-8 text-xs"
                            onClick={() => setIsOpen(false)}
                        >
                            Done
                        </Button>
                    </div>
                </div>
            </PopoverContent>
        </Popover>
    )
}

